// Shared UI theme: Silkscreen font stack, color palette, pixel-border panel
// styles, the HackerNoon pixel-icon helper, and the injected stylesheet that
// carries the mobile-first responsive layout for HUD and menus. The top HUD
// is the chip-rail layout (variant A): a 3px XP strip pinned to the very top
// edge, then a top-left rail of 24px vitals chips and a top-right rail of
// wave / timer / kills chips; continuous meters are 2px underline fills
// inside their chips (structure in hud.ts). Phone base ~11px UI font, with
// step-ups at 768px and 1600px and short-landscape compaction.

export const FONT_UI = `'Silkscreen', monospace`;

export const colors = {
  text: '#e8f4ff',
  muted: '#8fb3cc',
  accent: '#ffd36b', // pal.ui.accent — Lumensurge gold (GDD §11)
  xp: '#7dff4f', // pal.ui.xp — coins, motes, and the level-up bar (one family)
  hp: '#ff4d6b',
  panelBg: 'rgba(10, 27, 46, 0.92)',
  panelBorder: '#1e5b7a',
  barTrack: '#071526',
} as const;

// Lumensurge tier meter thresholds in collected mote value (GDD §9.5.1).
// Gate 1 display-only: the sim has no surge meter yet, so the HUD derives
// bar progress from mote value collected this run (see game.ts score).
export const SURGE_TIER_THRESHOLDS: readonly number[] = [0, 15, 35, 60, 90, 125, 165, 210, 260, 315];

/**
 * Stepped-corner "pixel frame" rendered as a box-shadow value.
 * The four offset copies leave the diagonal corner pixels empty, which reads
 * as a chunky 8-bit border. Returns only the value (no property name).
 */
export function pixelShadow(color: string = colors.panelBorder, width = 3): string {
  return (
    `0 -${width}px 0 0 ${color}, 0 ${width}px 0 0 ${color}, ` +
    `-${width}px 0 0 0 ${color}, ${width}px 0 0 0 ${color}`
  );
}

/** Full CSS text for a pixel-bordered panel. Compose into style.cssText. */
export function pixelPanelCss(padding = 12): string {
  return `
    background: ${colors.panelBg};
    box-shadow: ${pixelShadow()};
    padding: ${padding}px;
  `;
}

/**
 * HackerNoon pixel icon as a DOM element with `hn hn-${name}` iconfont
 * classes (requires the iconfont.css imported in main.ts). Without `size`
 * the glyph inherits the surrounding font-size (used by the responsive
 * stylesheet); with `size` it is fixed to that px box.
 */
export function icon(name: string, size?: number): HTMLElement {
  const el = document.createElement('i');
  el.className = `hn hn-${name}`;
  el.setAttribute('aria-hidden', 'true');
  el.style.display = 'inline-block';
  el.style.flex = 'none';
  if (size !== undefined) {
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.style.fontSize = `${size}px`;
    el.style.lineHeight = '1';
  }
  return el;
}

/** 8×8 skull bitmap (`.` = empty) — the HackerNoon set has no skull glyph. */
export const SKULL_GLYPH: readonly string[] = [
  '.######.',
  '########',
  '##.##.##',
  '########',
  '.######.',
  '..####..',
  '.#.##.#.',
  '..####..',
];

/**
 * Tiny pixel bitmap rendered as a crisp-edged SVG (for glyphs the HackerNoon
 * iconfont lacks, e.g. the kills skull). Sizes to 1em like the iconfont, so
 * the responsive stylesheet scales it with its row; color is currentColor.
 */
export function pixelGlyph(rows: readonly string[]): HTMLElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${rows[0].length} ${rows.length}`);
  svg.setAttribute('shape-rendering', 'crispEdges');
  svg.setAttribute('aria-hidden', 'true');
  let d = '';
  rows.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      if (row[x] !== '.' && row[x] !== ' ') d += `M${x} ${y}h1v1h-1z`;
    }
  });
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', d);
  path.setAttribute('fill', 'currentColor');
  svg.appendChild(path);
  svg.style.display = 'inline-block';
  svg.style.flex = 'none';
  svg.style.width = '1em';
  svg.style.height = '1em';
  return svg as unknown as HTMLElement;
}

/** Per-run score snapshot shared by the HUD and the game-over panel. */
export interface RunScore {
  kills: number;
  pickups: number;
  /** Total mote value collected this run; drives the Lumensurge bar. */
  motes: number;
  timeBonus: number;
  /** Bravery bonus banked by calling waves early. */
  bravery: number;
  /** Wave-clear bonuses banked on wave finalization. */
  waves: number;
  /** Denial bonuses for killing gestating (telegraphing) enemies. */
  gestation: number;
  /** Points earned in the wormhole subspace (multiplier-value kills). */
  subspace: number;
  /** Score spent in the supply-drop shop (the wallet: total = earned − spent). */
  spent: number;
  total: number;
}

const STYLE_ID = 'la-ui-styles';

/**
 * Injects the shared UI stylesheet once (idempotent). Mobile-first: the base
 * rules target 360–414px phones (~11px UI font), with step-ups at 768px and
 * 1600px. Below 480px the HUD collapses into compact full-width strips (the
 * corner panels dissolve into them via display: contents; hud.ts parks the
 * score row in the bottom bar). All interactive elements keep a 44px minimum
 * touch target, and panels respect env(safe-area-inset-*) notches.
 */
export function ensureUiStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
/* Lumen Abyss shared UI — see src/ui/theme.ts. Mobile-first base rules. */
.la-hud, .la-menu-root {
  font-family: ${FONT_UI};
  color: ${colors.text};
  text-shadow: 0 2px 0 rgba(0, 0, 0, 0.7);
}
.la-hud {
  position: absolute; inset: 0; pointer-events: none; font-size: 11px;
}
/* Positioning contexts for the HUD layers. Zero-height absolute bars at the
   viewport edges; the top HUD is the chip rails (below), the bottom bar
   carries the captain corner module. */
.la-hud-top { position: absolute; top: 0; left: 0; right: 0; height: 0; }
.la-hud-bottom { position: absolute; bottom: 0; left: 0; right: 0; height: 0; }
.la-panel {
  position: absolute; display: flex; flex-direction: column; gap: 5px;
  background: ${colors.panelBg}; box-shadow: ${pixelShadow()};
  padding: 8px 10px; box-sizing: border-box;
  max-width: calc(100vw - 20px);
  max-width: calc(100vw - 20px - env(safe-area-inset-left) - env(safe-area-inset-right));
}
.la-pos-bl {
  left: 10px; bottom: 10px;
  left: calc(10px + env(safe-area-inset-left));
  bottom: calc(10px + env(safe-area-inset-bottom));
}
.la-row { display: flex; align-items: center; gap: 6px; }
.la-muted { color: ${colors.muted}; }
.la-accent { color: ${colors.accent}; }
.la-small { font-size: 9px; }

/* Chip-rail top HUD (structure in hud.ts). The 3px XP strip pinned to the
   very top edge is the Lumensurge meter; the star + level badge hangs off
   its right end. Rails sit 20px below the top edge (clear of strip + badge)
   and hold rows of 24px chips on an 8px gap grid; each corner keeps at most
   two rows. On phones the right rail shifts 64px left to clear the floating
   touch pause button (input/touch-controls.ts). */
.la-xp-strip {
  position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: ${colors.barTrack};
}
.la-xp-fill {
  position: absolute; inset: 0;
  transform-origin: left center; transform: scaleX(0);
  background: ${colors.xp};
}
.la-rail {
  position: absolute; display: flex; flex-direction: column; gap: 8px;
  max-width: calc(100vw - 16px); box-sizing: border-box;
}
.la-rail-tl {
  left: 8px; top: 20px;
  left: calc(8px + env(safe-area-inset-left));
  top: calc(20px + env(safe-area-inset-top));
}
.la-rail-tr {
  right: 8px; top: 20px;
  right: calc(8px + env(safe-area-inset-right));
  top: calc(20px + env(safe-area-inset-top));
}
.la-rail-row { display: flex; align-items: center; gap: 8px; }
.la-rail-tr .la-rail-row { justify-content: flex-end; }
.la-chip {
  position: relative; display: inline-flex; align-items: center; gap: 4px;
  height: 24px; box-sizing: border-box; padding: 0 6px; flex: none;
  background: ${colors.panelBg}; box-shadow: ${pixelShadow(colors.panelBorder, 2)};
}
/* Vitals color-coding (A/B judge: identical frames made the bolt/square
   glyphs ambiguous) — each vitals chip carries a 2px accent border in its
   resource color: HP red, hull cyan, flash gold, level gold. */
.la-chip-hp { box-shadow: ${pixelShadow(colors.hp, 2)}; }
.la-chip-hull { box-shadow: ${pixelShadow('#35e0ff', 2)}; }
.la-chip-flash { box-shadow: ${pixelShadow(colors.accent, 2)}; }
.la-chip-level { box-shadow: ${pixelShadow(colors.accent, 2)}; }
.la-hud-icon {
  width: 16px; height: 16px; flex: none; display: block;
  image-rendering: pixelated;
}
.la-chip-under {
  position: absolute; left: 4px; right: 4px; bottom: 0; height: 2px;
  transform-origin: left center; transform: scaleX(0);
}
/* Rail CTAs (CALL / SHOP): gold-filled inverse chips (structure in hud.ts);
   the press sink is the tactile read that they are buttons, not counters. */
.la-rail-btn:active { transform: translateY(1px); filter: brightness(0.85); }
/* Wordy chip labels (WORMHOLE / SPACE STORM / serenity tier) only earn
   their width where there's room for them; phones show the icon alone. */
.la-chip-label { display: none; }
/* Enemies / score chips: below 480px the bottom stats strip carries these. */
.la-chip-opt { display: none; }
@media (min-width: 480px) {
  .la-chip-opt { display: inline-flex; }
}
@media (min-width: 768px) {
  .la-chip-label { display: inline; }
  .la-chip { padding: 0 8px; }
}
.la-fps { color: ${colors.muted}; }

/* Captain comms chip (widget built in captain/speech.ts): hosted IN FLOW
   inside the bottom-left corner module (hud.ts hostCaptainSpeech), so it
   needs no positioning here — the spring pop just grows from the tail
   corner. (An earlier fixed-position design parked it above the module with
   left/bottom offsets; those offsets must NOT come back — with the chip now
   position:relative they would lift it out of its layout slot and leave the
   module an empty shell.) */
.la-captain-speech {
  transform-origin: bottom left;
}

/* Onboarding hint chip (ui/onboarding.ts): non-modal first-run beat, parked
   in the bottom third above the captain module; it never takes input. */
.la-onboard {
  position: absolute; left: 50%; bottom: 32%; transform: translateX(-50%);
  max-width: calc(100vw - 48px); box-sizing: border-box;
  background: ${colors.panelBg}; box-shadow: ${pixelShadow()};
  padding: 8px 12px; text-align: center;
  font-family: ${FONT_UI}; font-size: 11px; color: ${colors.text};
  text-shadow: 0 2px 0 rgba(0, 0, 0, 0.7);
  pointer-events: none; z-index: 20;
}

.la-menu-root {
  position: absolute; inset: 0; display: none; box-sizing: border-box;
  align-items: center; justify-content: center;
  background: rgba(5, 10, 20, 0.6); font-size: 11px;
  padding: env(safe-area-inset-top) env(safe-area-inset-right)
           env(safe-area-inset-bottom) env(safe-area-inset-left);
  z-index: 50;
}
.la-menu-panel {
  display: none; flex-direction: column; box-sizing: border-box;
  background: ${colors.panelBg}; box-shadow: ${pixelShadow(colors.panelBorder, 4)};
  padding: 18px 16px; width: min(320px, 100%);
  max-width: calc(100vw - 24px);
  max-width: calc(100vw - 24px - env(safe-area-inset-left) - env(safe-area-inset-right));
  max-height: calc(100vh - 24px);
  max-height: calc(100vh - 24px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  max-height: calc(100dvh - 24px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  overflow-y: auto; overscroll-behavior: contain; pointer-events: auto;
}
.la-menu-title {
  margin: 0 0 12px; font-size: 16px; font-weight: 700; color: ${colors.accent};
  display: flex; align-items: center; justify-content: center; gap: 10px;
}
.la-btn {
  display: flex; align-items: center; gap: 10px; width: 100%;
  min-height: 44px; box-sizing: border-box;
  margin: 5px 0; padding: 8px 12px;
  background: ${colors.barTrack}; color: ${colors.text};
  border: 0; border-radius: 0; box-shadow: ${pixelShadow()};
  font-family: inherit; font-size: inherit; text-align: left;
  cursor: pointer; pointer-events: auto;
  touch-action: manipulation; user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.la-btn:hover, .la-btn:focus-visible {
  box-shadow: ${pixelShadow(colors.accent)}; color: ${colors.accent}; outline: none;
}
/* Pause menu variant B "Bands": one full-width vertical rhythm at every
   width — a gold title band bled to the panel edges, an identity strip
   (captain + ship portraits with names, seed + COPY flush right), a
   segmented stats band (2×2 on phones, 4-up at 480px+), then the action
   list with ABANDON split off by a divider. Structure in menus.ts. */
.la-pause-panel { gap: 12px; }
.la-pause-titleband {
  display: flex; align-items: center; justify-content: space-between;
  /* Bleed to the panel edges; band side padding matches the panel's so
     the text aligns with the bands below. */
  margin: -18px -16px 0; padding: 4px 16px;
  background: ${colors.accent}; color: ${colors.barTrack};
  font-size: 12px; font-weight: 700; letter-spacing: 2px;
  /* No global text-shadow on dark text over the gold band. */
  text-shadow: none;
}
.la-pause-title-left { display: inline-flex; align-items: center; gap: 8px; }
.la-pause-esc-hint { display: none; font-size: 8px; letter-spacing: 2px; }
.la-pause-identity {
  display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 8px 16px;
}
.la-pause-crew { display: flex; align-items: flex-start; gap: 24px; }
.la-pause-captain, .la-pause-ship {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
}
/* Portraits read as framed crew cards: big enough to anchor the band, names
   on small plates beneath (A/B: 48px left-aligned read timid + appended). */
.la-pause-captain .la-memorial-canvas,
.la-pause-ship .la-memorial-canvas { width: 64px; height: 64px; }
.la-pause-crew-name {
  font-size: 8px; letter-spacing: 2px; color: ${colors.muted}; text-align: center;
  padding: 2px 8px; background: ${colors.barTrack};
  box-shadow: inset 0 0 0 2px ${colors.panelBorder};
  max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
/* Seed + COPY: a ruled footer strip under the stats band — seed text left,
   COPY right; integrated into the record section at every width. */
.la-pause-seed {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding-top: 8px; border-top: 2px solid ${colors.panelBorder};
  font-size: 8px; letter-spacing: 1px; color: ${colors.muted};
}
.la-pause-copy {
  background: ${colors.barTrack}; color: ${colors.accent};
  border: 0; box-shadow: inset 0 0 0 2px ${colors.panelBorder};
  font-family: inherit; font-size: 8px; letter-spacing: 1px;
  padding: 4px 8px; cursor: pointer; pointer-events: auto;
  touch-action: manipulation;
}
.la-pause-copy:hover, .la-pause-copy:focus-visible {
  box-shadow: inset 0 0 0 2px ${colors.accent};
  color: ${colors.accent};
  outline: none;
}
/* Stats band: the container paints the cell dividers through the grid gaps
   (no outer frame — the full border read as border-dense in the A/B review);
   each cell is a dark fill with a big value over a small uppercase label. */
.la-pause-panel .la-pause-stats {
  display: grid; grid-template-columns: repeat(2, 1fr);
  gap: 2px; padding: 0; margin: 0;
  background: ${colors.panelBorder};
}
.la-pause-stats .la-stat {
  flex-direction: column; align-items: center; justify-content: center;
  gap: 4px; padding: 8px 4px;
  background: ${colors.barTrack};
}
.la-pause-stats .la-stat-value {
  color: ${colors.accent}; font-size: 14px; font-weight: 700; letter-spacing: 1px;
}
.la-pause-stats .la-stat-label {
  flex: none; color: ${colors.muted}; font-size: 8px; letter-spacing: 2px;
}
@media (min-width: 480px) {
  .la-pause-panel .la-pause-stats { grid-template-columns: repeat(4, 1fr); }
}
.la-pause-actions { display: flex; flex-direction: column; gap: 8px; }
.la-pause-actions .la-btn { margin: 0; }
/* Stacked pause actions read cleaner with inset frames; the generic
   pixel-shadow border floats outside the dark fills and looks misaligned
   against this dense layout. */
.la-pause-actions .la-btn {
  box-shadow: inset 0 0 0 2px ${colors.panelBorder};
}
.la-pause-actions .la-btn:hover, .la-pause-actions .la-btn:focus-visible {
  box-shadow: inset 0 0 0 2px ${colors.accent};
}
.la-pause-actions .la-btn.primary,
.la-pause-actions .la-btn.primary:hover,
.la-pause-actions .la-btn.primary:focus-visible {
  box-shadow: inset 0 0 0 2px ${colors.accent};
}
/* Destructive action stays last and visually subordinate: shorter than the
   48px stack, muted frame until hovered (A/B judge: equal footprint competed
   with the secondary actions). */
.la-pause-actions .la-btn.danger {
  min-height: 40px;
  box-shadow: inset 0 0 0 2px rgba(255, 77, 107, 0.5);
}
.la-pause-actions .la-btn.danger:hover,
.la-pause-actions .la-btn.danger:focus-visible {
  box-shadow: inset 0 0 0 2px #ff4d6b;
}
.la-pause-divider { border-top: 2px solid ${colors.panelBorder}; }
/* ESC hint on RESUME is a desktop affordance (no Escape key on phones). */
.la-pause-resume .la-btn-hint { display: none; }
.la-btn-hint {
  margin-left: auto; font-size: 8px; letter-spacing: 2px;
  color: ${colors.muted};
}
.la-btn.primary .la-btn-hint { color: ${colors.barTrack}; opacity: 0.7; }
@media (min-width: 768px) {
  /* Two-class: beats the .la-menu-panel width (min 360px) further down. */
  .la-menu-panel.la-pause-panel { width: min(640px, 100%); }
  .la-pause-titleband {
    margin: -22px -26px 0; padding: 8px 26px;
    font-size: 14px; letter-spacing: 4px;
  }
  /* Desktop identity: portraits scale up to hold their own against the
     panel width and action labels (A/B judge: 48px read timid). */
  .la-pause-captain .la-memorial-canvas,
  .la-pause-ship .la-memorial-canvas { width: 80px; height: 80px; }
  .la-pause-crew-name { font-size: 9px; max-width: 160px; }
  .la-pause-esc-hint { display: inline; }
  .la-pause-resume .la-btn-hint { display: inline; }
  .la-pause-actions .la-btn { white-space: nowrap; }
  .la-pause-stats .la-stat-value { font-size: 16px; }
}
@media (min-width: 1600px) {
  .la-pause-titleband { margin: -26px -30px 0; padding: 8px 30px; }
}
/* Short landscape phones: compact the bands so the whole stack fits (menus
   still scroll if a long name pushes it over). Keep the bleed in sync with
   the compacted .la-menu-panel padding. */
@media (orientation: landscape) and (max-height: 500px) and (max-width: 1599px) {
  .la-pause-panel { gap: 8px; }
  .la-pause-titleband { margin: -12px -14px 0; padding: 4px 14px; }
  .la-pause-captain .la-memorial-canvas,
  .la-pause-ship .la-memorial-canvas { width: 48px; height: 48px; }
  .la-pause-stats .la-stat { padding: 4px; }
  .la-pause-actions { gap: 4px; }
}
.la-defeat-panel {
  gap: 0; padding: 0;
  background: rgba(16, 10, 35, 0.97);
  box-shadow: ${pixelShadow('#5e2f8a', 4)};
  overflow: hidden;
}
.la-defeat-titleband {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px;
  background: #e35c82; color: #140c24;
  font-weight: 700; letter-spacing: 4px;
  /* The global menu text-shadow is meant for light-on-dark; under this dark
     text on the pink band it reads as a smudge (playtest report). */
  text-shadow: none;
}
.la-defeat-title-copy { font-size: 16px; }
.la-defeat-run-copy { font-size: 8px; letter-spacing: 3px; opacity: 0.9; }
.la-defeat-body { display: flex; flex-direction: column; }
.la-defeat-memorial {
  display: flex; flex-direction: column; gap: 14px;
  padding: 16px;
}
.la-defeat-media-row {
  display: flex; align-items: flex-end; justify-content: center; gap: 16px;
}
.la-defeat-media-block {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
}
.la-defeat-media-block.is-portrait .la-memorial-canvas {
  width: 72px; height: 72px;
  box-shadow: ${pixelShadow(colors.accent)};
}
.la-defeat-media-block.is-ship .la-memorial-canvas {
  width: 92px; height: 72px;
  box-shadow: none; background: transparent;
}
.la-defeat-media-label {
  color: #9f90bf; font-size: 8px; letter-spacing: 2px;
}
.la-defeat-name {
  color: ${colors.accent};
  font-size: 14px; font-weight: 700; letter-spacing: 2px; line-height: 1.4;
}
.la-defeat-line {
  color: ${colors.text};
  line-height: 1.5;
}
.la-defeat-dna-card {
  display: inline-flex; flex-direction: column; gap: 6px;
  align-self: flex-start;
  padding: 10px 14px;
  color: #79c4ff;
  background: rgba(10, 21, 50, 0.9);
  box-shadow: ${pixelShadow('#2260a7', 3)};
}
.la-defeat-dna-overline {
  color: #9db2d8; font-size: 8px; letter-spacing: 2px;
}
.la-defeat-dna-label {
  display: inline-flex; align-items: center; gap: 8px;
  color: #79c4ff; font-size: 11px; letter-spacing: 2px;
}
.la-defeat-stats {
  margin: 0;
  padding: 16px;
  gap: 0;
  border-top: 2px solid rgba(94, 47, 138, 0.8);
  background: rgba(20, 14, 45, 0.95);
}
.la-defeat-stats .la-stat {
  padding: 10px 0;
  border-bottom: 1px solid rgba(92, 102, 152, 0.3);
}
.la-defeat-stats .la-stat:last-child { border-bottom: 0; }
.la-defeat-stats .la-stat-label {
  color: #9db2d8; letter-spacing: 2px;
}
.la-defeat-stats .la-stat-value {
  color: ${colors.text};
}
.la-defeat-stats .la-stat.is-bonus .la-stat-value { color: ${colors.accent}; }
.la-defeat-stats .la-stat.is-penalty .la-stat-value { color: ${colors.hp}; }
.la-defeat-bonus-section {
  margin-top: 10px;
  padding-top: 12px;
  border-top: 2px solid rgba(34, 96, 167, 0.7);
}
.la-defeat-stats .la-stat.is-score {
  margin-top: 12px;
  padding-top: 16px;
  border-top: 2px solid rgba(255, 211, 107, 0.8);
  border-bottom: 0;
  align-items: flex-end;
}
.la-defeat-stats .la-stat.is-score .la-stat-label {
  color: ${colors.accent};
}
.la-defeat-stats .la-stat.is-score .la-stat-value {
  color: ${colors.accent};
  font-size: 24px;
  letter-spacing: 2px;
}
.la-defeat-cta {
  width: calc(100% - 32px);
  margin: 0 16px 16px;
  min-height: 56px;
}
.la-defeat-cta .la-btn-hint { display: inline; }
@media (min-width: 768px) {
  .la-menu-panel.la-defeat-panel {
    width: min(980px, calc(100% - 32px));
    max-width: calc(100vw - 48px - env(safe-area-inset-left) - env(safe-area-inset-right));
  }
  .la-defeat-titleband {
    padding: 18px 28px;
  }
  .la-defeat-title-copy { font-size: 18px; }
  .la-defeat-run-copy { font-size: 10px; }
  .la-defeat-body {
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
    align-items: stretch;
  }
  .la-defeat-memorial {
    padding: 28px 32px 34px;
    border-right: 2px solid rgba(94, 47, 138, 0.8);
    min-height: 100%;
  }
  .la-defeat-media-row {
    justify-content: flex-start;
    gap: 24px;
  }
  .la-defeat-media-block.is-portrait .la-memorial-canvas {
    width: 108px; height: 108px;
  }
  .la-defeat-media-block.is-ship .la-memorial-canvas {
    width: 136px; height: 108px;
  }
  .la-defeat-name {
    font-size: 18px;
    margin-top: 4px;
  }
  .la-defeat-line {
    max-width: 34ch;
    font-size: 13px;
  }
  .la-defeat-stats {
    padding: 28px 32px 20px;
  }
  .la-defeat-stats .la-stat {
    padding: 12px 0;
  }
  .la-defeat-stats .la-stat.is-score {
    margin-top: auto;
    padding-top: 20px;
  }
  .la-defeat-stats .la-stat.is-score .la-stat-value {
    font-size: 40px;
  }
  .la-defeat-cta {
    width: calc(100% - 56px);
    margin: 0 28px 28px;
    min-height: 72px;
    font-size: 16px;
  }
}
.la-menu-btnrow { display: flex; gap: 6px; width: 100%; margin: 5px 0; }
.la-menu-btnrow .la-btn {
  flex: 1 1 0; justify-content: center; gap: 6px;
  margin: 0; padding: 8px 4px; min-width: 0;
}
.la-btn.primary {
  background: ${colors.accent}; color: ${colors.barTrack};
  box-shadow: ${pixelShadow(colors.accent)};
}
.la-btn.primary:hover, .la-btn.primary:focus-visible {
  background: ${colors.accent}; color: ${colors.barTrack};
  box-shadow: ${pixelShadow(colors.accent, 5)};
}
.la-btn.danger, .la-btn.danger:hover, .la-btn.danger:focus-visible {
  box-shadow: ${pixelShadow('#ff4d6b')}; color: #ff4d6b;
}
.la-field { display: block; width: 100%; margin: 8px 0; pointer-events: auto; }
.la-field-label { margin-bottom: 2px; }
.la-field input[type='range'] {
  display: block; width: 100%; height: 44px; margin: 0;
  accent-color: ${colors.accent}; cursor: pointer; touch-action: pan-y;
}
.la-check {
  display: flex; align-items: center; gap: 10px;
  min-height: 44px; margin: 4px 0;
  cursor: pointer; pointer-events: auto; touch-action: manipulation;
  user-select: none; -webkit-tap-highlight-color: transparent;
}
.la-check input {
  width: 22px; height: 22px; flex: none;
  accent-color: ${colors.accent}; cursor: pointer;
}
.la-captain-memorial {
  margin: 0 0 12px; text-align: center;
}
.la-captain-memorial-row {
  display: flex; align-items: center; justify-content: center; gap: 14px;
}
.la-captain-memorial-mid { text-align: center; }
.la-memorial-canvas {
  width: 64px; height: 64px; image-rendering: pixelated; flex: none;
  background: ${colors.barTrack}; box-shadow: ${pixelShadow(colors.accent)};
}
.la-captain-memorial-name {
  color: ${colors.accent}; font-weight: 700; letter-spacing: 2px;
}
.la-captain-memorial-line {
  color: ${colors.text}; font-style: italic; margin-top: 4px;
}
.la-captain-memorial-dna {
  color: ${colors.muted}; font-size: 8px; letter-spacing: 2px; margin-top: 6px;
}
.la-stats { display: flex; flex-direction: column; gap: 5px; margin: 0 0 12px; }
.la-stat { display: flex; align-items: center; gap: 10px; }
.la-stat-label { flex: 1; color: ${colors.muted}; }
.la-stat-value { color: ${colors.accent}; }
/* Mobile bottom stats strip (design v3): full-width row above the pilot
   seat — bolt ×tier, bug enemies, trophy score left; skull kills right.
   Hidden at 480px+ where the top bars carry these. */
.la-stats-strip {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; padding: 1px 0 3px; font-size: 9px;
}
.la-stats-strip-group { display: inline-flex; align-items: center; gap: 10px; }
.la-stats-strip-item { display: inline-flex; align-items: center; gap: 4px; }
@media (min-width: 480px) {
  .la-stats-strip { display: none; }
}

/* iOS haptic overlay switch (makeHapticSwitch in input/haptics.ts): a
   native <input switch> laid invisibly over a control — a genuine tap
   toggles it natively, firing the system haptic tick with no JS on the
   haptic path (iOS 26.5-safe). The wrapper's own click handler forwards
   activation to the control's logic. */
.la-haptic-wrap { position: relative; display: flex; }
.la-haptic-wrap > * { flex: 1 1 auto; }
.la-haptic-tick {
  position: absolute; inset: 0; width: 100%; height: 100%;
  flex: none; opacity: 0; margin: 0; padding: 0; cursor: pointer;
  pointer-events: auto;
  -webkit-tap-highlight-color: transparent;
}
.la-credits {
  margin-top: 14px; font-size: 8px; line-height: 1.6;
  color: ${colors.muted}; text-align: center; max-width: 100%;
}

/* Phones (<480px): the chip rails are already the mobile layout — the only
   phone-specific tweak is shifting the right rail and the XP badge 64px left
   to clear the floating touch pause button. The corner module (pilot seat +
   captain speech) keeps its panel frame and stretches full-width (design v3)
   so the stats strip and the speech bubble span the screen; kills fold into
   the strip, so the bottom kill row hides. */
@media (max-width: 479px) {
  .la-hud { font-size: 10px; }
  .la-rail-tr {
    right: 64px;
    right: calc(64px + env(safe-area-inset-right));
  }
  .la-hud-bottom .la-panel {
    display: flex;
    left: 8px;
    left: calc(8px + env(safe-area-inset-left));
    right: 8px;
    right: calc(8px + env(safe-area-inset-right));
    bottom: 8px;
    bottom: calc(8px + env(safe-area-inset-bottom));
  }
  .la-kill-row { display: none; }
  .la-small { font-size: 8px; }
}

/* Tablet / small desktop step-up. */
@media (min-width: 768px) {
  .la-hud { font-size: 13px; }
  .la-panel { gap: 6px; padding: 10px 12px; }
  .la-small { font-size: 11px; }
  .la-menu-root { font-size: 13px; }
  .la-menu-panel { padding: 22px 26px; width: min(360px, 100%); }
  .la-menu-title { font-size: 20px; gap: 12px; }
  .la-credits { font-size: 9px; }
}

/* Large desktop step-up. */
@media (min-width: 1600px) {
  .la-hud { font-size: 16px; }
  .la-panel { gap: 8px; padding: 14px 16px; }
  .la-small { font-size: 13px; }
  .la-menu-root { font-size: 16px; }
  .la-menu-panel { padding: 26px 30px; width: min(460px, 100%); }
  .la-menu-title { font-size: 28px; }
  .la-credits { font-size: 11px; }
}

/* Short landscape phones: shrink chrome so more of the playfield stays
   visible. Touch targets stay at 44px; menus scroll instead. */
@media (orientation: landscape) and (max-height: 500px) and (max-width: 1599px) {
  .la-hud { font-size: 10px; }
  .la-panel { gap: 4px; padding: 6px 8px; }
  .la-menu-root { font-size: 10px; }
  .la-menu-panel { padding: 12px 14px; }
  .la-menu-title { font-size: 14px; margin-bottom: 8px; }
  .la-btn { margin: 3px 0; }
  .la-field { margin: 4px 0; }
  .la-check { margin: 0; }
  .la-credits { margin-top: 8px; }
}
`;
  document.head.appendChild(style);
}
